import { CSSProperties, Ref, useEffect, useRef, useState } from "react";

import { addDays, addMonths, differenceInDays, getDay } from "date-fns";
import flatMap from "lodash/flatMap";
import maxBy from "lodash/maxBy";
import minBy from "lodash/minBy";
import { ChevronDown, ChevronRight } from "lucide-react";
import { ItemRendererProps } from "node_modules/react-calendar-timeline/dist/lib/items/Item";
import Timeline, {
  DateHeader,
  ItemContext,
  TimelineHeaders,
  TimelineMarkers,
  TodayMarker,
} from "react-calendar-timeline";
import { useMediaQuery } from "react-responsive";

import { isMdQuery, isXsQuery } from "@/components/layout/Media";
import {
  getGroupTimeline,
  getItemTimeline,
  TimelineItem,
} from "@/components/pages_logged_in/program/components/program-timeline/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { STANDARD_NAV_HEIGHT } from "@/css/constants";
import { LeadComponent, ParticipantComponent } from "@/types/programs";
import { formatDate } from "@/utils/datetime";

import { ComponentCard } from "./ComponentCard";
import { TimelineMode } from "./types";

interface GetResizePropsDirection {
  ref: Ref<HTMLDivElement>;
  className: string;
  style: CSSProperties;
}

// Define label formats
const labelFormats = {
  longMonth: "MMMM",
  longDay: "MMM d",
  month: "MMM",
  day: "d",
  week: "MMM d",
};

interface EventItemProps {
  programId: string;
  item: TimelineItem;
  itemContext: ItemContext;
  getItemProps: ItemRendererProps<TimelineItem>["getItemProps"];
  leftResizeProps: GetResizePropsDirection;
  rightResizeProps: GetResizePropsDirection;
  isExpanded?: boolean;
  onExpanded?: (value: boolean) => void;
}

function EventItem({
  programId,
  item,
  itemContext,
  getItemProps,
  leftResizeProps,
  rightResizeProps,
  isExpanded,
  onExpanded,
}: EventItemProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { key, ...otherProps } = getItemProps({
    style: {
      borderRadius: "6px",
      position: "relative",
      minWidth: 26,
      background: item.background,
      borderColor: "transparent",
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "6px 10px",
    },
  });

  return (
    <div key={key} {...otherProps}>
      {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : ""}

      {item.children?.length ? (
        isExpanded ? (
          <ChevronDown
            className="h-4 w-4 cursor-pointer"
            style={{ color: item.textColor }}
            onClick={() => onExpanded?.(false)}
          />
        ) : (
          <ChevronRight
            className="h-4 w-4 cursor-pointer"
            style={{ color: item.textColor }}
            onClick={() => onExpanded?.(true)}
          />
        )
      ) : null}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div
            className="flex items-center gap-1 cursor-pointer w-full h-full relative"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
          >
            <div className="flex-shrink-0">{item.icon}</div>
            <div
              className="text-xs font-medium truncate flex-1"
              style={{ color: item.textColor }}
            >
              {item.title}
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="end"
          className="rct-item-content p-0 w-auto"
        >
          {item.component && (
            <ComponentCard programId={programId} component={item.component} />
          )}
        </PopoverContent>
      </Popover>

      {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : ""}
    </div>
  );
}

interface TimelineViewProps {
  programId: string;
  components: LeadComponent[] | ParticipantComponent[];
  viewMode: TimelineMode;
  onViewModeChange: (viewMode: string) => void;
}

export function TimelineView({
  programId,
  components,
  viewMode,
  onViewModeChange,
}: TimelineViewProps) {
  const timelineRef = useRef<Timeline>(null);

  const verticalLineClassNamesForTime = (start: number) => {
    const dayOfWeek = getDay(start); // 0 for Sunday, 6 for Saturday
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return ["weekend"];
    }
    return [];
  };

  const determineLabelFormat = (unit: string, labelWidth?: number) => {
    if (unit === "month") {
      if (labelWidth && labelWidth > 80) {
        return labelFormats.longMonth;
      }
      return labelFormats.month;
    } else if (unit === "week") {
      return labelFormats.week;
    } else if (labelWidth && labelWidth > 55) {
      return labelFormats.longDay;
    } else {
      return labelFormats.day;
    }
  };

  const items = getItemTimeline({ components: components });

  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const visibleItems = flatMap(items, (item) => {
    const isExpanded = expandedItems.includes(item.id.toString());
    if (isExpanded && item.children) {
      return [item, ...item.children];
    }

    return item;
  }).map((item, index) => {
    return {
      ...item,
      group: index + 1,
    };
  });
  const visibleChildItems = visibleItems.filter((visibleItem) =>
    items.every((item) => item.id !== visibleItem.id),
  );
  const [groups, childGroups] = getGroupTimeline({
    items: visibleItems,
    childItems: visibleChildItems,
  });

  const calculateAddItemNumber = (
    viewMode: TimelineMode,
    dayDiff: number,
    monthDiff: number,
  ) => {
    if (viewMode === "month" && monthDiff < 2) {
      return 1.5;
    }

    if (viewMode === "day") {
      if (dayDiff < 2) return (2 - dayDiff) / 2 + 1;
      if (dayDiff < 4) return (4 - dayDiff) / 2 + 1;
      if (dayDiff < 6) return (6 - dayDiff) / 2 + 1;
      if (dayDiff < 8) return (8 - dayDiff) / 2 + 1;
      return 7;
    }

    return 1;
  };

  const getMinMaxTime = () => {
    const minItem = minBy(visibleItems, "start_time");
    const maxItem = maxBy(visibleItems, "end_time");
    const dayDiff =
      maxItem?.end_time && minItem?.start_time
        ? differenceInDays(
            new Date(maxItem.end_time),
            new Date(minItem.start_time),
          )
        : 0;
    const monthDiff = dayDiff / 30;
    const weekDiff = dayDiff / 7;
    // Add more day / month to display component name
    // Add extra day to timeline switch to month view, day view
    const addItemNumber = calculateAddItemNumber(viewMode, dayDiff, monthDiff);

    return {
      minTime: minItem?.start_time
        ? addDays(new Date(minItem.start_time), -addItemNumber)
        : new Date(),
      maxTime: maxItem?.end_time
        ? addDays(new Date(maxItem.end_time), addItemNumber)
        : new Date(),
      dayDiff,
      weekDiff,
      monthDiff,
    };
  };
  const { minTime, maxTime, weekDiff, monthDiff } = getMinMaxTime();

  const getOngoingComponent = () => {
    const statusPriority = ["Ongoing", "Upcoming", "Completed"];

    for (const status of statusPriority) {
      const component = visibleItems.find(
        (item) => item.componentStatus === status,
      );
      if (component) {
        return component;
      }
    }

    return visibleItems[0];
  };

  const isXs = useMediaQuery(isXsQuery);
  const isMd = useMediaQuery(isMdQuery);

  const displayTimeView = () => {
    // If has ongoing component, view current time
    const ongoingComponent = getOngoingComponent();

    let viewTime = new Date();

    if (
      ongoingComponent?.componentStatus !== "Ongoing" &&
      ongoingComponent?.start_time
    ) {
      viewTime = new Date(ongoingComponent?.start_time);
    }

    let defaultTimeStart = minTime;
    let defaultTimeEnd = maxTime;
    const expandWeek = isXs ? 2 : isMd ? 4 : 6;
    const expandMonth = isXs ? 4 : isMd ? 5 : 6;

    if (viewMode === "day" && weekDiff > expandWeek) {
      defaultTimeStart = addDays(viewTime, -expandWeek / 2);
      defaultTimeEnd = addDays(viewTime, expandWeek / 2);
    }

    if (viewMode === "month" && monthDiff > expandMonth) {
      defaultTimeStart = addMonths(viewTime, -expandMonth / 2);
      defaultTimeEnd = addMonths(viewTime, expandMonth / 2);
    }

    return {
      defaultTimeStart,
      defaultTimeEnd,
    };
  };

  const { defaultTimeStart, defaultTimeEnd } = displayTimeView();

  useEffect(() => {
    if (timelineRef.current) {
      timelineRef.current.updateScrollCanvas(
        defaultTimeStart.valueOf(),
        defaultTimeEnd.valueOf(),
      );
    }
  }, [viewMode]);

  // Force resize event after initial render
  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, []);

  return (
    <Timeline<TimelineItem>
      ref={timelineRef}
      groups={groups}
      items={visibleItems}
      defaultTimeStart={defaultTimeStart.valueOf()}
      defaultTimeEnd={defaultTimeEnd.valueOf()}
      onTimeChange={(
        visibleTimeStart,
        visibleTimeEnd,
        updateScrollCanvas,
        unit,
      ) => {
        const dayDiff = differenceInDays(
          new Date(visibleTimeEnd),
          new Date(visibleTimeStart),
        );
        if (unit !== viewMode || (unit === "day" && dayDiff > 62)) {
          if (unit === "day" && dayDiff > 62) {
            // Force to month view if day diff > 62
            onViewModeChange("month");
          } else {
            onViewModeChange(unit);
          }
        } else {
          if (
            new Date(visibleTimeStart) < minTime &&
            new Date(visibleTimeEnd) > maxTime
          ) {
            updateScrollCanvas(minTime.valueOf(), maxTime.valueOf());
          } else if (new Date(visibleTimeStart) < minTime) {
            updateScrollCanvas(
              minTime.valueOf(),
              minTime.valueOf() +
                (new Date(visibleTimeEnd).valueOf() -
                  new Date(visibleTimeStart).valueOf()),
            );
          } else if (new Date(visibleTimeEnd) > maxTime) {
            updateScrollCanvas(
              maxTime.valueOf() -
                (new Date(visibleTimeEnd).valueOf() -
                  new Date(visibleTimeStart).valueOf()),
              maxTime.valueOf(),
            );
          } else {
            updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
          }
        }
      }}
      minZoom={7 * 24 * 60 * 60 * 1000} // Minimum zoom to 7 days
      maxZoom={5 * 31 * 24 * 60 * 60 * 1000} // Maximum zoom to 5 months
      itemHeightRatio={0.6}
      sidebarWidth={0}
      lineHeight={44}
      verticalLineClassNamesForTime={verticalLineClassNamesForTime}
      horizontalLineClassNamesForGroup={({ id }) => {
        return childGroups.some((group) => group.id === id)
          ? ["bg-gray-100"]
          : [];
      }}
      itemRenderer={({ item, itemContext, getItemProps, getResizeProps }) => {
        const { left: leftResizeProps, right: rightResizeProps } =
          getResizeProps();
        const isExpanded = expandedItems.includes(item.id.toString());

        return (
          <EventItem
            item={item}
            programId={programId}
            itemContext={itemContext}
            getItemProps={getItemProps}
            leftResizeProps={leftResizeProps}
            rightResizeProps={rightResizeProps}
            isExpanded={isExpanded}
            onExpanded={(value) => {
              if (value) {
                setExpandedItems((prev) => [...prev, item.id.toString()]);
              } else {
                setExpandedItems((prev) =>
                  prev.filter((id) => id !== item.id.toString()),
                );
              }
            }}
          />
        );
      }}
    >
      <TimelineHeaders
        style={{
          position: "sticky",
          top: STANDARD_NAV_HEIGHT,
          background: "#FFF",
          zIndex: 99,
        }}
      >
        <DateHeader
          unit="month"
          height={44}
          labelFormat={([startTime], unit, labelWidth) => {
            const format = determineLabelFormat(unit, labelWidth);
            return formatDate(startTime.toDate(), format) || "";
          }}
          intervalRenderer={({ getIntervalProps, intervalContext }) => {
            return (
              <div
                className="h-11 border-l border-b border-gray-200 flex justify-center items-center"
                style={getIntervalProps().style}
              >
                {intervalContext.intervalText}
              </div>
            );
          }}
        />

        {viewMode === "day" && (
          <DateHeader
            unit="day"
            height={44}
            labelFormat={([startTime], unit, labelWidth) => {
              const format = determineLabelFormat(unit, labelWidth);
              return formatDate(startTime.toDate(), format) || "";
            }}
            intervalRenderer={({ getIntervalProps, intervalContext }) => {
              return (
                <div
                  className="h-11 border-l border-gray-200 flex justify-center items-center"
                  style={getIntervalProps().style}
                >
                  {intervalContext.intervalText}
                </div>
              );
            }}
          />
        )}
      </TimelineHeaders>
      <TimelineMarkers>
        <TodayMarker>
          {({ styles }) => {
            const customStyles = {
              ...styles,
              backgroundColor: "#3788EE",
              width: "2px",
            };
            return <div style={customStyles} onClick={() => {}} />;
          }}
        </TodayMarker>
      </TimelineMarkers>
    </Timeline>
  );
}
